import React, { useCallback, useEffect, useRef, useState } from 'react'
import MaskedInput, { type Mask } from 'react-text-mask'
import clsx from 'classnames'
import styles from './ShadowInput.module.scss'

type ShadowInputProps = {
  value?: string,
  onChange?: (value: string) => void,
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void,
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void,
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void,
  type?: string,
  mask?: Mask | false,
  error?: boolean,
  dense?: boolean,
  className?: string,
  placeholder?: string,
  required?: boolean,
  disabled?: boolean,
  autoComplete?: string,
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'],
  lang?: string,
  shadowColor?: string,
  tooltip?: React.ReactNode,
}

const CAPTURE = { capture: true }

const isTextField = (el: HTMLElement) => {
  const tag = el.tagName.toLowerCase()
  return tag === 'input' || tag === 'textarea'
}

/** Text input with a coloured shadow and an optional mask */
export const ShadowInput: React.FC<ShadowInputProps> = React.memo((props) => {
  const {
    value = '', onChange, onFocus, onBlur, onPaste, type = 'text', mask = false, error = false, dense = false,
    className, placeholder, required, disabled, autoComplete, inputMode, lang, shadowColor, tooltip,
  } = props
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const touchMoveHappened = useRef(false)

  // On touch devices a tap outside closes the keyboard and does not reach what is under it
  const handleDocumentTouchEnd = useCallback((e: TouchEvent) => {
    if (touchMoveHappened.current) {
      touchMoveHappened.current = false
      return
    }
    const target = e.target as HTMLElement
    const targetIsTextField = isTextField(target)
    if (inputRef.current && target !== inputRef.current && !targetIsTextField) {
      inputRef.current.blur()
    }
    if (!targetIsTextField
      && target.tagName.toLowerCase() !== 'button'
      && !target.closest('[data-active-element]')) {
      e.stopPropagation()
      e.preventDefault()
    }
  }, [])

  const handleDocumentTouchMove = useCallback(() => {
    touchMoveHappened.current = true
  }, [])

  const removeListeners = useCallback(() => {
    document.removeEventListener('touchend', handleDocumentTouchEnd, CAPTURE)
    document.removeEventListener('touchmove', handleDocumentTouchMove, CAPTURE)
  }, [handleDocumentTouchEnd, handleDocumentTouchMove])

  useEffect(() => removeListeners, [removeListeners])

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true)
    onFocus?.(e)
    setTimeout(() => {
      document.addEventListener('touchmove', handleDocumentTouchMove, CAPTURE)
      document.addEventListener('touchend', handleDocumentTouchEnd, CAPTURE)
    }, 0)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false)
    onBlur?.(e)
    setTimeout(removeListeners, 0)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value)
  }

  const setInputRef = useCallback((element: MaskedInput | null) => {
    if (element) {
      inputRef.current = element.inputElement as HTMLInputElement
    }
  }, [])

  return (
    <div className={clsx([styles.inputContainer, className, { [styles.error]: error, [styles.dense]: dense }])}>
      <MaskedInput
        lang={lang}
        keepCharPositions={false}
        ref={setInputRef}
        mask={mask}
        value={value}
        className={styles.input}
        type={type}
        placeholder={placeholder}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        onPaste={onPaste}
        required={required}
        showMask={focused}
        disabled={disabled}
        autoComplete={autoComplete}
        inputMode={inputMode}
      />
      <div data-color={shadowColor} className={styles.shadow} />
      <span className={styles.tooltip}>{tooltip}</span>
    </div>
  )
})
ShadowInput.displayName = 'ShadowInput'
